import axios from 'axios';
import { toast } from './toast';

const defaultOptions = {
  baseURL: process.env.REACT_APP_WINDFALL_API_BASE_URL,
};

export const client = axios.create(defaultOptions);

// Marcador que o interceptor anexa a erros ja tratados globalmente (ex: o toast
// de rate limit). Paginas com catch proprio checam via wasHandledGlobally para
// nao mostrar um toast generico por cima.
type HandledError = { handledGlobally?: boolean };

export const wasHandledGlobally = (error: unknown): boolean =>
  !!(error as HandledError | null)?.handledGlobally;

// O backend passou a exigir um JWT valido em toda rota nao marcada como @Public
// (login e recuperacao de senha). Este interceptor anexa o token guardado no
// localStorage em todas as requisicoes.
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ID fixo para o toast de rate limit: com dedupe o Chakra nao empilha varios
// toasts identicos quando uma rajada de requisicoes toma 429 de uma vez.
const RATE_LIMIT_TOAST_ID = 'rate-limit';

// Tratamento global de auth e rate limiting.
// - 401 (token invalido/expirado ou usuario desativado/removido no banco):
//   desloga e volta para o login.
// - 429 (ThrottlerGuard do backend: 100 req/60s global; login e send-code
//   5/60s; update-password 10/60s): mostra um toast amigavel. Respeita o
//   header Retry-After quando presente.
// - 403 (perfil sem permissao) e apenas repassado para o chamador tratar.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('@fId');
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    if (status === 429) {
      (error as HandledError).handledGlobally = true;

      if (!toast.isActive(RATE_LIMIT_TOAST_ID)) {
        const retryAfter = Number(error.response?.headers?.['retry-after']);
        const espera =
          Number.isFinite(retryAfter) && retryAfter > 0
            ? ` Tente novamente em ${retryAfter} segundo${retryAfter > 1 ? 's' : ''}.`
            : ' Aguarde um instante e tente novamente.';

        toast({
          id: RATE_LIMIT_TOAST_ID,
          title: 'Muitas tentativas.',
          description: `Você fez requisições demais em pouco tempo.${espera}`,
          status: 'warning',
          isClosable: true
        });
      }
    }

    return Promise.reject(error);
  }
);
